import { useRef, useEffect, useState } from 'react';
import { useIdleTimer, IIdleTimer } from 'react-idle-timer';
import { EventsType } from 'react-idle-timer';

type UseInactivityTimeoutOptions = {
    timeout: number; // em minutos
    disabled?: boolean;
    onTimeout: () => void;
};

const FOCUSED_ACTIVITY_EVENTS: EventsType[] = [
    'mousemove',
    'keydown',
    'wheel',
    'DOMMouseScroll',
    'mousewheel',
    'mousedown',
    'touchstart',
    'touchmove',
    'MSPointerDown',
    'MSPointerMove'
];

export const useInactivityTimeout = ({
    timeout,
    onTimeout,
    disabled = false
}: UseInactivityTimeoutOptions) => {
    const idleTimerRef = useRef<IIdleTimer>(null);

    const [isWindowFocused, setIsWindowFocused] = useState(
        disabled ? false : document.hasFocus()
    );

    const onIdle = () => {
        if (disabled) return;

        onTimeout();
    };

    useEffect(() => {
        if (disabled) return;

        const handleFocus = () => {
            setIsWindowFocused(true);
            idleTimerRef.current?.reset();
        };

        const handleBlur = () => {
            setIsWindowFocused(false);
        };

        window.addEventListener('focus', handleFocus);
        window.addEventListener('blur', handleBlur);

        return () => {
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('blur', handleBlur);
        };
    }, [disabled]);

    const activityEvents: EventsType[] = [];

    useIdleTimer({
        ref: idleTimerRef,
        onIdle,
        timeout: timeout * 60 * 1000,
        promptBeforeIdle: timeout <= 0 ? -1 : 5000,
        crossTab: true,
        leaderElection: true,
        debounce: 500,
        disabled: disabled,
        events: isWindowFocused ? FOCUSED_ACTIVITY_EVENTS : activityEvents
    });

    const resetTimer = () => {
        if (disabled) return;

        idleTimerRef.current?.reset();
    };

    return { resetTimer };
};
